import React, { useCallback, useEffect, useRef, useState } from 'react'

import { trackAiChatScreenViewedPayload } from 'generated/trackingEvents'
import {
  useEndChatSessionMutation,
  useStartChatSessionMutation,
} from 'generated/types'
import useAnalyticsComponentViewed from 'hooks/useAnalyticsComponentViewed'

import { ChatContent } from './ChatContent'
import { MessagesHeader } from './MessagesHeader'
import { ChatContainer } from './styled-components'
import { AI_CHAT_FEEDBACK_ORIGIN } from './utils/constants'

export const ChatInterface = ({
  showAIAssistant,
  setShowAIAssistant,
}: {
  showAIAssistant: boolean
  setShowAIAssistant: (showAIAssistant: boolean) => void
}) => {
  const chatSessionInitializedRef = useRef(false)
  const [sessionId, setSessionId] = useState<string | undefined>(undefined)
  const [hasError, setHasError] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)

  const ref = useAnalyticsComponentViewed({
    generatedPayload: trackAiChatScreenViewedPayload({
      origin: AI_CHAT_FEEDBACK_ORIGIN,
    }),
  })

  const [endChatSession] = useEndChatSessionMutation()
  const [startChatSession] = useStartChatSessionMutation({
    onCompleted(data) {
      if (data.startChatSession.__typename === 'ChatSession') {
        setSessionId(data.startChatSession.id)
        setHasError(false)
      } else {
        setHasError(true)
      }
    },
    onError() {
      setHasError(true)
    },
  })

  const initializeSession = useCallback(() => {
    if (sessionId) {
      endChatSession({
        variables: {
          sessionId,
        },
      })
    }

    startChatSession()
  }, [startChatSession, endChatSession, sessionId])

  useEffect(() => {
    if (!chatSessionInitializedRef.current && showAIAssistant) {
      chatSessionInitializedRef.current = true
      initializeSession()
    }
  }, [initializeSession, showAIAssistant])

  const handleReinitializeSession = () => {
    setHasError(false)
    initializeSession()
  }

  const handleEndChatSession = () => {
    endChatSession({ variables: { sessionId: sessionId || '' } })
  }

  // TODO: Ask Caitlin if we should be calling endChatSession here
  const handleCloseAssistant = () => {
    setShowAIAssistant(false)
  }

  if (!showAIAssistant) {
    return null
  }

  return (
    <ChatContainer isMinimized={isMinimized} ref={ref}>
      <MessagesHeader
        setShowAIAssistant={setShowAIAssistant}
        endChatSession={handleEndChatSession}
        isMinimized={isMinimized}
        setIsMinimized={setIsMinimized}
      />
      <ChatContent
        hasError={hasError}
        onReinitializeSession={handleReinitializeSession}
        onCloseAssistant={handleCloseAssistant}
        sessionId={sessionId}
        isMinimized={isMinimized}
      />
    </ChatContainer>
  )
}
